import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PreviewRow, guestAPI } from '../../services/guestAPI';
import { useAppSelector } from '../../store/hooks';
import { 
  ChevronLeft, ChevronRight, Check, X, AlertTriangle, 
  Trash2, Edit2, Save, XCircle, CheckCircle, Users, Loader2
} from 'lucide-react';
import Button from '../UI/Button';
import ConfirmationModal from '../UI/ConfirmationModal';
import { BillingActionPreview, CostBreakdown } from '../Billing';
import { billingService, PricingConfiguration, HostBalance } from '../../services/billingService';
import { logError } from '../../utils/errorLogger';

interface GuestImportPreviewProps {
  eventId: string;
  rows: PreviewRow[];
  onRowsChange: (rows: PreviewRow[]) => void;
  summary: {
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
    duplicate_rows: number;
  };
  onConfirm: () => void;
  onCancel: () => void;
  onReupload: () => void;
  loading?: boolean;
}

const ROWS_PER_PAGE = 25;

const GuestImportPreview: React.FC<GuestImportPreviewProps> = ({
  eventId,
  rows,
  onRowsChange,
  summary,
  onConfirm,
  onCancel,
  onReupload,
  loading = false,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<PreviewRow['parsed_data'] | null>(null);
  const [selectAll, setSelectAll] = useState(true);
  const [validatingRowId, setValidatingRowId] = useState<number | null>(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  
  // Billing state
  const [pricing, setPricing] = useState<PricingConfiguration | null>(null);
  const [balance, setBalance] = useState<HostBalance | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Pagination
  const totalPages = Math.ceil(rows.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const currentRows = rows.slice(startIndex, endIndex);

  // Calculate dynamic summary from current rows (recalculates when rows change)
  const dynamicSummary = useMemo(() => {
    const validRows = rows.filter(r => r.validation.is_valid).length;
    const invalidRows = rows.filter(r => !r.validation.is_valid).length;
    const duplicateRows = rows.filter(r => r.is_duplicate).length;
    return {
      total_rows: rows.length,
      valid_rows: validRows,
      invalid_rows: invalidRows,
      duplicate_rows: duplicateRows,
    };
  }, [rows]);

  // Count selected valid rows
  const selectedValidCount = useMemo(() => {
    return rows.filter(r => r.selected && r.validation.is_valid && !r.is_duplicate).length;
  }, [rows]);

  const selectedCount = useMemo(() => {
    return rows.filter(r => r.selected).length;
  }, [rows]);

  // Fetch billing data
  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setBillingLoading(true);
        const [pricingData, balanceData] = await Promise.all([
          billingService.getCurrentPricing().catch(() => null),
          billingService.getMyBalance().catch(() => null)
        ]);
        setPricing(pricingData);
        setBalance(balanceData);
      } catch (error) {
        logError('Failed to fetch billing data', error);
      } finally {
        setBillingLoading(false);
      }
    };
    fetchBillingData();
  }, []);

  // Count companions for a row: Companion 1–5 Name columns + legacy companion_name
  const countCompanionsForRow = useCallback((row: PreviewRow): number => {
    const pd = row.parsed_data;
    let count = 0;
    for (let n = 1; n <= 5; n++) {
      const nameKey = `companion ${n} name`;
      const val = pd[nameKey];
      if (val != null && String(val).trim() && String(val).toLowerCase() !== 'nan') count++;
    }
    // Legacy single companion_name (only if not already counted as companion 1 name)
    const comp1Name = pd['companion 1 name'];
    const hasComp1 = comp1Name != null && String(comp1Name).trim();
    if (!hasComp1 && row.parsed_data.companion_name?.trim()) count++;
    return count;
  }, []);

  // Get companion field for display (Companion N Name/Phone/Relationship or legacy for N=1)
  const getCompanionField = useCallback((row: PreviewRow, n: number, field: 'name' | 'phone' | 'relationship'): string => {
    const key = `companion ${n} ${field}`;
    const val = row.parsed_data[key];
    if (val != null && String(val).trim() && String(val).toLowerCase() !== 'nan') return String(val).trim();
    if (n === 1) {
      if (field === 'name') return row.parsed_data.companion_name?.trim() || '';
      if (field === 'phone') return row.parsed_data.companion_phone?.trim() || '';
      return row.parsed_data.relationship?.trim() || '';
    }
    return '';
  }, []);

  // Calculate costs for import
  const calculateImportCosts = useCallback((): CostBreakdown[] => {
    if (!pricing || selectedValidCount === 0) return [];

    let guestCount = 0;
    let companionCount = 0;

    rows.filter(r => r.selected && r.validation.is_valid && !r.is_duplicate).forEach(row => {
      guestCount++;
      companionCount += countCompanionsForRow(row);
    });

    const costs: CostBreakdown[] = [];
    
    if (guestCount > 0) {
      costs.push({
        label: `Guest Base Fee (${guestCount} guest${guestCount !== 1 ? 's' : ''})`,
        amount: guestCount * parseFloat(pricing.guest_base_fee),
        quantity: guestCount,
        unitPrice: parseFloat(pricing.guest_base_fee)
      });
    }

    if (companionCount > 0) {
      costs.push({
        label: `Companion Fee (${companionCount} companion${companionCount !== 1 ? 's' : ''})`,
        amount: companionCount * parseFloat(pricing.companion_fee),
        quantity: companionCount,
        unitPrice: parseFloat(pricing.companion_fee)
      });
    }

    return costs;
  }, [pricing, selectedValidCount, rows, countCompanionsForRow]);

  // Toggle row selection
  const toggleRowSelection = useCallback((rowNumber: number) => {
    const newRows = rows.map(r => 
      r.row_number === rowNumber ? { ...r, selected: !r.selected } : r
    );
    onRowsChange(newRows);
  }, [rows, onRowsChange]);

  // Toggle all selection
  const toggleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newRows = rows.map(r => ({ ...r, selected: newSelectAll }));
    onRowsChange(newRows);
  }, [selectAll, rows, onRowsChange]);

  // Start editing a row
  const startEditing = useCallback((row: PreviewRow) => {
    setEditingRowId(row.row_number);
    setEditedData({ ...row.parsed_data });
  }, []);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingRowId(null);
    setEditedData(null);
  }, []);

  // Save edited row - calls backend for validation
  const saveEditing = useCallback(async () => {
    if (editingRowId === null || !editedData) return;

    // Capture current values before async operation
    const rowIdToValidate = editingRowId;
    const dataToValidate = { ...editedData };
    
    setValidatingRowId(rowIdToValidate);
    // Clear editing state immediately to prevent stale data issues
    setEditingRowId(null);
    setEditedData(null);
    
    try {
      // Call backend to validate the edited data
      const response = await guestAPI.validateRow(eventId, dataToValidate);
      
      if (response.success) {
        // Always use the validation result from backend
        const newRows = rows.map(r => {
          if (r.row_number === rowIdToValidate) {
            return {
              ...r,
              parsed_data: response.parsed_data,
              validation: {
                is_valid: response.validation.is_valid,
                errors: response.validation.errors || [],
                warnings: response.validation.warnings || [],
                error_fields: response.validation.error_fields || [],
                warning_fields: response.validation.warning_fields || [],
              },
            };
          }
          return r;
        });
        
        onRowsChange(newRows);
      } else {
        // If API call failed, keep the edited data but mark as invalid
        const newRows = rows.map(r => {
          if (r.row_number === rowIdToValidate) {
            return {
              ...r,
              parsed_data: dataToValidate,
              validation: {
                is_valid: false,
                errors: [response.error || 'Validation failed'],
                warnings: [],
                error_fields: [],
                warning_fields: [],
              },
            };
          }
          return r;
        });
        onRowsChange(newRows);
      }
    } catch (error) {
      console.error('Error validating row:', error);
      // On error, keep data but mark as needing review
      const newRows = rows.map(r => {
        if (r.row_number === rowIdToValidate) {
          return {
            ...r,
            parsed_data: dataToValidate,
            validation: {
              is_valid: false,
              errors: ['Failed to validate - please try again'],
              warnings: [],
              error_fields: [],
              warning_fields: [],
            },
          };
        }
        return r;
      });
      onRowsChange(newRows);
    } finally {
      setValidatingRowId(null);
    }
  }, [editingRowId, editedData, rows, onRowsChange, eventId]);

  // Remove a row
  const removeRow = useCallback((rowNumber: number) => {
    const newRows = rows.filter(r => r.row_number !== rowNumber);
    onRowsChange(newRows);
  }, [rows, onRowsChange]);

  // Handle input change in edit mode (field can be standard key or companion N key e.g. "companion 1 name")
  const handleEditChange = useCallback((field: keyof PreviewRow['parsed_data'] | string, value: string) => {
    if (!editedData) return;
    setEditedData({ ...editedData, [field]: value });
  }, [editedData]);

  // Check if a field has an error or warning
  const hasFieldError = (row: PreviewRow, field: string) => {
    return row.validation.error_fields?.includes(field);
  };

  const hasFieldWarning = (row: PreviewRow, field: string) => {
    return row.validation.warning_fields?.includes(field);
  };

  // Render cell content (editable or display)
  const renderCell = (row: PreviewRow, field: keyof PreviewRow['parsed_data']) => {
    const fieldStr = String(field);
    const isEditing = editingRowId === row.row_number;
    const value = isEditing && editedData ? editedData[field] : row.parsed_data[field];
    const isErrorField = hasFieldError(row, fieldStr);
    const isWarningField = hasFieldWarning(row, fieldStr);

    if (isEditing) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => handleEditChange(field, e.target.value)}
          className={`w-full px-2 py-1 text-xs rounded border ${
            isErrorField
              ? 'border-red-500 bg-red-100 dark:bg-red-900/50'
              : isWarningField
              ? 'border-yellow-500 bg-yellow-100 dark:bg-yellow-900/50'
              : theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
      );
    }

    // Get cell background based on error/warning status
    const cellBgClass = isErrorField
      ? theme === 'dark' ? 'bg-red-900/60 rounded px-1' : 'bg-red-200 rounded px-1'
      : isWarningField
      ? theme === 'dark' ? 'bg-yellow-900/60 rounded px-1' : 'bg-yellow-200 rounded px-1'
      : '';

    // Get tooltip text for error/warning fields
    const tooltipText = isErrorField
      ? row.validation.errors.find(e => e.toLowerCase().includes(fieldStr.replace('_', ' ')))
      : isWarningField
      ? row.validation.warnings.find(w => w.toLowerCase().includes(fieldStr.replace('_', ' ')))
      : value;

    return (
      <span 
        className={`text-xs truncate block ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'} ${cellBgClass}`} 
        title={tooltipText || value}
      >
        {value || '-'}
      </span>
    );
  };

  // Render editable companion cell (Companion N Name / Phone / Relationship)
  const renderCompanionCell = (
    row: PreviewRow,
    n: number,
    field: 'name' | 'phone' | 'relationship'
  ) => {
    const key = `companion ${n} ${field}` as keyof PreviewRow['parsed_data'];
    const fieldStr: string = `companion ${n} ${field}`;
    const isEditing = editingRowId === row.row_number;
    const value = isEditing && editedData
      ? (editedData[key] ?? '')
      : getCompanionField(row, n, field);
    const isErrorField = hasFieldError(row, fieldStr);
    const isWarningField = hasFieldWarning(row, fieldStr);

    if (isEditing) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => handleEditChange(key, e.target.value)}
          className={`w-full px-2 py-1 text-xs rounded border ${
            isErrorField
              ? 'border-red-500 bg-red-100 dark:bg-red-900/50'
              : isWarningField
              ? 'border-yellow-500 bg-yellow-100 dark:bg-yellow-900/50'
              : theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
      );
    }

    const cellBgClass = isErrorField
      ? theme === 'dark' ? 'bg-red-900/60 rounded px-1' : 'bg-red-200 rounded px-1'
      : isWarningField
      ? theme === 'dark' ? 'bg-yellow-900/60 rounded px-1' : 'bg-yellow-200 rounded px-1'
      : '';
    const tooltipText = isErrorField
      ? row.validation.errors.find(e => e.toLowerCase().includes(fieldStr.replace('_', ' ')))
      : isWarningField
      ? row.validation.warnings.find(w => w.toLowerCase().includes(fieldStr.replace('_', ' ')))
      : value;

    return (
      <span
        className={`text-xs truncate block ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'} ${cellBgClass}`}
        title={tooltipText || value}
      >
        {value || '-'}
      </span>
    );
  };

  // Get row background color based on status
  const getRowBgClass = (row: PreviewRow) => {
    if (!row.validation.is_valid) {
      return theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50';
    }
    if (row.is_duplicate) {
      return theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-50';
    }
    // Highlight rows with warnings
    if (row.validation.warnings && row.validation.warnings.length > 0) {
      return theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50/70';
    }
    if (!row.selected) {
      return theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-100';
    }
    return '';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Summary Header */}
      <div className={`p-4 border-b ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <Users className={`h-4 w-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-500'}`} />
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>{dynamicSummary.total_rows} Total</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              theme === 'dark' ? 'bg-green-900/30' : 'bg-green-50'
            }`}>
              <CheckCircle className={`h-4 w-4 ${theme === 'dark' ? 'text-green-400' : 'text-green-500'}`} />
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>{dynamicSummary.valid_rows} Valid</span>
            </div>
            {dynamicSummary.invalid_rows > 0 && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'
              }`}>
                <XCircle className={`h-4 w-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`} />
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>{dynamicSummary.invalid_rows} Invalid</span>
              </div>
            )}
            {dynamicSummary.duplicate_rows > 0 && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-50'
              }`}>
                <AlertTriangle className={`h-4 w-4 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>{dynamicSummary.duplicate_rows} Duplicates</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <table className="w-full min-w-[1800px]">
          <thead className={`sticky top-0 z-10 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
          }`}>
            <tr>
              <th className="w-10 px-2 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className="rounded"
                />
              </th>
              <th className={`w-12 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>#</th>
              <th className={`w-32 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Full Name</th>
              <th className={`w-24 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>First Name</th>
              <th className={`w-16 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Title</th>
              <th className={`w-24 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Last Name</th>
              <th className={`w-28 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Phone</th>
              <th className={`w-32 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Email</th>
              {[1, 2, 3, 4, 5].map((n) => (
                <React.Fragment key={n}>
                  <th className={`w-24 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>Comp {n} Name</th>
                  <th className={`w-24 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>Comp {n} Phone</th>
                  <th className={`w-20 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>Comp {n} Rel</th>
                </React.Fragment>
              ))}
              <th className={`w-24 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Notes</th>
              <th className={`w-24 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
              <th className={`w-20 px-2 py-3 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row) => (
              <tr 
                key={`${row.row_number}-${row.validation.is_valid}-${row.validation.errors.length}`}
                className={`border-b transition-colors ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                } ${getRowBgClass(row)}`}
              >
                <td className="px-2 py-2">
                  <input
                    type="checkbox"
                    checked={row.selected}
                    onChange={() => toggleRowSelection(row.row_number)}
                    className="rounded"
                  />
                </td>
                <td className={`px-2 py-2 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{row.row_number}</td>
                <td className="px-2 py-2">{renderCell(row, 'full_name')}</td>
                <td className="px-2 py-2">{renderCell(row, 'first_name')}</td>
                <td className="px-2 py-2">{renderCell(row, 'title')}</td>
                <td className="px-2 py-2">{renderCell(row, 'last_name')}</td>
                <td className="px-2 py-2">{renderCell(row, 'phone')}</td>
                <td className="px-2 py-2">{renderCell(row, 'email')}</td>
                {[1, 2, 3, 4, 5].map((n) => (
                  <React.Fragment key={n}>
                    <td className={`px-2 py-2 ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50/50'}`}>
                      {renderCompanionCell(row, n, 'name')}
                    </td>
                    <td className={`px-2 py-2 ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50/50'}`}>
                      {renderCompanionCell(row, n, 'phone')}
                    </td>
                    <td className={`px-2 py-2 ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50/50'}`}>
                      {renderCompanionCell(row, n, 'relationship')}
                    </td>
                  </React.Fragment>
                ))}
                <td className="px-2 py-2">{renderCell(row, 'notes')}</td>
                <td className="px-2 py-2">
                  {!row.validation.is_valid ? (
                    <span 
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full cursor-help ${
                        theme === 'dark' ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                      }`}
                      title={row.validation.errors.join('\n')}
                    >
                      <X className="h-3 w-3" />
                      Error
                    </span>
                  ) : row.is_duplicate ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      theme === 'dark' ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      <AlertTriangle className="h-3 w-3" />
                      Duplicate
                    </span>
                  ) : (row.validation.warnings && row.validation.warnings.length > 0) ? (
                    <span 
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full cursor-help ${
                        theme === 'dark' ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                      }`}
                      title={row.validation.warnings.join('\n')}
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Warning
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      theme === 'dark' ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      <Check className="h-3 w-3" />
                      Valid
                    </span>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    {validatingRowId === row.row_number ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    ) : editingRowId === row.row_number ? (
                      <>
                        <button
                          onClick={saveEditing}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'hover:bg-green-900/50 text-green-400' : 'hover:bg-green-100 text-green-600'
                          }`}
                          title="Save & Validate"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                          }`}
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(row)}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          }`}
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4 text-blue-500" />
                        </button>
                        <button
                          onClick={() => removeRow(row.row_number)}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          }`}
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`flex items-center justify-between px-4 py-3 border-t ${
          theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        }`}>
          <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Showing {startIndex + 1} to {Math.min(endIndex, rows.length)} of {rows.length} rows
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded ${
                currentPage === 1
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded ${
                currentPage === totalPages
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className={`flex flex-wrap items-center justify-between gap-4 p-4 border-t ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReupload}
            disabled={loading}
          >
            Re-upload File
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {selectedCount} of {rows.length} selected
          </span>
          <Button
            variant="primary"
            size="sm"
            onClick={async () => {
              // Refresh billing data before showing modal to ensure it's up-to-date
              try {
                setBillingLoading(true);
                const [pricingData, balanceData] = await Promise.all([
                  billingService.getCurrentPricing().catch(() => null),
                  billingService.getMyBalance().catch(() => null)
                ]);
                setPricing(pricingData);
                setBalance(balanceData);
              } catch (error) {
                logError('Failed to fetch billing data', error);
              } finally {
                setBillingLoading(false);
                setShowImportConfirm(true);
              }
            }}
            disabled={loading || selectedValidCount === 0}
            className="min-w-[160px]"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Importing...
              </span>
            ) : (
              `Import ${selectedValidCount} Guest${selectedValidCount !== 1 ? 's' : ''}`
            )}
          </Button>
        </div>
      </div>

      {/* Import Confirmation Modal with Billing Preview */}
      <ConfirmationModal
        isOpen={showImportConfirm}
        onClose={() => setShowImportConfirm(false)}
        onConfirm={() => {
          setShowImportConfirm(false);
          onConfirm();
        }}
        title="Confirm Import"
        message=""
        confirmText="Confirm & Import"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={loading}
      >
        {!billingLoading && pricing && selectedValidCount > 0 ? (
          <BillingActionPreview
            costs={calculateImportCosts()}
            showBalance={true}
            showBalanceAfter={true}
            currentBalance={balance}
            pricing={pricing}
            showWarning={true}
            compact={false}
            title="Import Cost Preview"
            infoText="Fees are charged immediately when guests are imported."
          />
        ) : (
          <div className={`p-4 rounded-lg border ${
            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Loading cost information...
            </p>
          </div>
        )}
      </ConfirmationModal>
    </div>
  );
};

export default GuestImportPreview;
